// Rapprochement bancaire : croiser un relevé de banque avec ce qui est déjà
// inscrit dans le tableau « Compte bancaire » du CRM.
//
// Le problème que ça résout : la même opération est décrite avec des mots
// différents des deux côtés. Saisi à la main, un prélèvement s'appelle
// « Facture EDF » ; la banque l'écrit « Prélèvement - EDF SA - REF
// FR12ZZZ… ». Comparer les libellés reviendrait à ne jamais les reconnaître,
// et à créer un doublon à chaque import.
//
// Ce qui identifie réellement une opération, c'est LA DATE ET LE MONTANT.
// Le libellé ne sert qu'à départager deux opérations du même jour et du même
// montant, et à dire si le rapprochement est exact ou approchant.
//
// Fonction PURE : aucun accès réseau ni base, donc testable ligne à ligne.

/** Le minimum commun aux mouvements du CRM et à ceux d'un relevé. */
export interface MouvementComparable {
  date_mouvement: string; // AAAA-MM-JJ
  libelle: string;
  debit: number | string;
  credit: number | string;
}

export interface PaireRapprochee<C, R> {
  crm: C;
  releve: R;
}

export interface Rapprochement<C, R> {
  /** Même date, même montant, même libellé : rien à signaler. */
  identiques: PaireRapprochee<C, R>[];
  /**
   * Même date et même montant, libellé différent : la même opération, écrite
   * autrement. Surtout pas un doublon à créer.
   */
  rapproches: PaireRapprochee<C, R>[];
  /** Présents sur le relevé, inconnus du CRM : à ajouter. */
  nouveaux: R[];
  /**
   * Inscrits dans le CRM sur la période du relevé, mais absents de celui-ci.
   * Ce sont EUX qui méritent un coup d'œil : opération jamais passée en
   * banque, montant erroné, ou ligne saisie deux fois.
   */
  absents: C[];
}

/** Montant signé d'un mouvement, en centimes, pour comparer sans flottants. */
function centimes(m: MouvementComparable): number {
  return Math.round((Number(m.credit) - Number(m.debit)) * 100);
}

/** Libellé ramené à une forme comparable. */
function libelleNormalise(m: MouvementComparable): string {
  return m.libelle.trim().toUpperCase().replace(/\s+/g, ' ');
}

/** Clé d'identité : ce qui fait qu'une opération est LA même. */
function cle(m: MouvementComparable): string {
  return `${m.date_mouvement}|${String(centimes(m))}`;
}

function grouper<T extends MouvementComparable>(liste: T[]): Map<string, T[]> {
  const par = new Map<string, T[]>();
  for (const m of liste) {
    const k = cle(m);
    const l = par.get(k);
    if (l) l.push(m);
    else par.set(k, [m]);
  }
  return par;
}

/**
 * Croise les mouvements du CRM et ceux d'un relevé.
 *
 * `periode` borne les « absents » : sans elle, tout ce que contient le CRM en
 * dehors de la période couverte par le relevé serait signalé comme manquant.
 * Bornes incluses, au format AAAA-MM-JJ.
 */
export function rapprocherMouvements<C extends MouvementComparable, R extends MouvementComparable>(
  crm: C[],
  releve: R[],
  periode?: { debut: string; fin: string } | null,
): Rapprochement<C, R> {
  const disponibles = grouper(crm);
  const identiques: PaireRapprochee<C, R>[] = [];
  const rapproches: PaireRapprochee<C, R>[] = [];
  const nouveaux: R[] = [];
  const consommes = new Set<C>();

  // Deux passes. La première épuise les correspondances exactes, pour que le
  // libellé identique l'emporte quand plusieurs opérations partagent le même
  // jour et le même montant ; la seconde rapproche ce qui reste.
  const enAttente: R[] = [];
  for (const r of releve) {
    const candidats = disponibles.get(cle(r));
    const exact = candidats?.find(
      (c) => !consommes.has(c) && libelleNormalise(c) === libelleNormalise(r),
    );
    if (exact) {
      consommes.add(exact);
      identiques.push({ crm: exact, releve: r });
    } else {
      enAttente.push(r);
    }
  }

  for (const r of enAttente) {
    const candidats = disponibles.get(cle(r));
    const approchant = candidats?.find((c) => !consommes.has(c));
    if (approchant) {
      consommes.add(approchant);
      rapproches.push({ crm: approchant, releve: r });
    } else {
      nouveaux.push(r);
    }
  }

  const dansLaPeriode = (m: C): boolean =>
    !periode || (m.date_mouvement >= periode.debut && m.date_mouvement <= periode.fin);
  const absents = crm.filter((m) => !consommes.has(m) && dansLaPeriode(m));

  return { identiques, rapproches, nouveaux, absents };
}
